import { Client } from "@stomp/stompjs";
import type { IMessage } from "@stomp/stompjs";

export type ConnectionStatus = "disconnected" | "connecting" | "connected" | "error";

// 여기서만 정한다. STOMP 는 없는 목적지 구독도 조용히 성공하므로, 서버가 목적지를
// 옮겼을 때 한쪽만 고치면 에러 없이 실시간이 죽는다.
// `/user/queue` 는 세션 Principal 로 수신자를 고른다 — 인증이 안 서면 구독은 되는데
// 메시지가 안 온다.
export const STOMP_DESTINATIONS = {
  events: "/user/queue/events",
  eventVideos: "/user/queue/event-videos",
} as const;

type StatusListener = (status: ConnectionStatus) => void;

let client: Client | null = null;
let currentStatus: ConnectionStatus = "disconnected";
const listeners = new Set<StatusListener>();

function getWebSocketUrl(): string {
  const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${wsProtocol}//${window.location.host}/api/stomp/platform`;
}

function setStatus(status: ConnectionStatus) {
  currentStatus = status;
  for (const listener of listeners) {
    try {
      listener(status);
    } catch (e) {
      void e;
    }
  }
}

export const stompService = {
  connect(onStatusChange?: StatusListener) {
    if (onStatusChange) {
      listeners.add(onStatusChange);
      onStatusChange(currentStatus);
    }
    if (client?.active) return;

    const instance = new Client({
      brokerURL: getWebSocketUrl(),
      reconnectDelay: 5000,
      debug: () => {},
      onConnect: () => {
        if (client === instance) setStatus("connected");
      },
      onDisconnect: () => {
        if (client === instance) setStatus("disconnected");
      },
      onStompError: () => {
        if (client === instance) setStatus("error");
      },
      onWebSocketClose: () => {
        if (client === instance) setStatus("disconnected");
      },
    });
    client = instance;

    setStatus("connecting");
    instance.activate();
  },

  subscribe(destination: string, handler: (message: IMessage) => void): () => void {
    if (!client?.connected) {
      return () => {};
    }

    const subscription = client.subscribe(destination, handler);
    return () => subscription.unsubscribe();
  },

  disconnect(onStatusChange?: StatusListener) {
    if (onStatusChange) listeners.delete(onStatusChange);
    if (listeners.size > 0) return;
    if (client?.active) {
      client.deactivate();
    }
    client = null;
    currentStatus = "disconnected";
  },

  get connected() {
    return client?.connected ?? false;
  },
};
